import axios from 'axios';
import React, { Fragment, useEffect, useState, useMemo } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

function OrderDetailsPage() {
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(false); // cancel action loading
    const [fetching, setFetching] = useState(true); // initial fetch loading
    const { id } = useParams();
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL;

    useEffect(() => {
        let active = true;
        const load = async () => {
            try {
                const res = await axios.get(`${API_URL}/order-details/${id}`);
                if (active) setOrder(res.data || {});
            } catch (e) {
                toast.error('Failed to fetch order details. Redirecting...');
                setTimeout(() => navigate('/my-orders'), 1800);
            } finally {
                if (active) setFetching(false);
            }
        };
        load();
        return () => { active = false; };
    }, [API_URL, id, navigate]);

    const handleCancel = async () => {
        if (!order || order.status === 'cancelled') return;
        if (!window.confirm('Cancel this order? This cannot be undone.')) return;
        setLoading(true);
        try {
            await axios.put(`${API_URL}/update-order-status/${id}`, { status: 'cancelled' });
            toast.success('Order cancelled');
            setOrder(o => ({ ...o, status: 'cancelled' }));
        } catch (e) {
            toast.error('Cancellation failed, try again.');
        } finally {
            setLoading(false);
        }
    };

    // Derived data
    const items = order?.items || [];
    const subtotal = useMemo(() => items.reduce((acc, it) => acc + (it.price * it.quantity), 0), [items]);
    const payable = order?.totalAmount || subtotal;
    const savings = order?.appliedOffer ? Math.max(0, subtotal - payable) : 0;
    const offerLabel = useMemo(() => {
        const off = order?.appliedOffer;
        if (!off) return '';
        const { code, name, unit, value } = off;
        if (code) return code;
        if (name) return name;
        if (value) return unit === 'Percent' ? `${value}% OFF` : `₹${value} OFF`;
        return '';
    }, [order]);
    const formattedAddress = useMemo(() => {
        if (!order?.address) return '';
        const { name, house, area, landmark, city } = order.address;
        return [name, house, area, landmark, city].filter(Boolean).join(', ');
    }, [order]);

    const statusBadge = (status) => {
        if (!status) return 'Pending';
        const s = status.toLowerCase();
        if (s === 'cancelled') return 'Cancelled';
        if (s === 'delivered') return 'Delivered';
        if (s === 'shipped') return 'Shipped';
        return status;
    };

    const handleDownloadInvoice = () => {
        if (!order) return;
        try {
            const doc = new jsPDF();
            const orderIdShort = order._id ? `KIPPS-${order._id.slice(-5)}` : 'N/A';
            const dateStr = order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
            const addr = formattedAddress;
            const fmt = (n) => `Rs ${n}`; // Avoid Unicode rupee symbol to prevent stray glyph

            // Header
            doc.setFontSize(16);
            doc.setFont('helvetica', 'bold');
            doc.text('KIPPS SWEETS - INVOICE', 105, 16, { align: 'center' });
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text(`Order: ${orderIdShort}`, 14, 26);
            doc.text(`Date: ${dateStr}`, 14, 32);
            doc.text(`Status: ${statusBadge(order.status)}`, 14, 38);
            doc.text(`Mobile: ${order.mobile || ''}`, 14, 44);
            doc.text('Billing / Delivery Address:', 14, 54);
            doc.text(addr || '-', 14, 60, { maxWidth: 180 });

            // Items table
            const body = items.map(it => [it.name || it.title, it.quantity, fmt(it.price), fmt(it.price * it.quantity)]);
            const startY = 70;
            autoTable(doc, {
                head: [['Product', 'Qty', 'Price', 'Total']],
                body,
                startY,
                styles: { fontSize: 9 },
                headStyles: { fillColor: [237, 50, 55] }
            });

            let y = (doc.lastAutoTable && doc.lastAutoTable.finalY ? doc.lastAutoTable.finalY : startY) + 6;
            doc.setFont('helvetica', 'bold');
            doc.text('Summary', 14, y);
            doc.setFont('helvetica', 'normal');
            y += 6;
            doc.text(`Subtotal: ${fmt(subtotal)}`, 14, y); y += 5;
            if (savings > 0) { doc.text(`You Save: -${fmt(savings)}`, 14, y); y += 5; }
            if (offerLabel) { doc.text(`Offer Applied: ${offerLabel}`, 14, y); y += 5; }
            doc.text(`Payable Amount: ${fmt(payable)}`, 14, y); y += 8;
            doc.setFontSize(8);
            doc.text('Thank you for shopping with Kipps!', 14, y);

            doc.save(`Invoice-${orderIdShort}.pdf`);
        } catch (e) {
            console.error(e);
            toast.error('Failed to generate invoice.');
        }
    };

    return (
        <Fragment>
            <div className='container-fluid m-t25'>
                <div className='row'>
                    <div className='col-12 cat-strip border shadow-sm'>
                        <div>
                            <button onClick={() => navigate(-1)}><IoMdArrowRoundBack /></button>
                            <label>ORDER DETAILS</label>
                        </div>
                    </div>
                </div>
            </div>
            <div className='container-fluid'>
                <div className='row'>
                    <div className='col-xl-10 col-lg-11 col-md-12 col-12 mx-auto'>
                        {fetching ? (
                            <div className='text-center mt-4 mb-5'>
                                <div className='spinner-border text-danger' role='status'><span className='visually-hidden'>Loading...</span></div>
                            </div>
                        ) : (
                            <div className='order-summary-card mt-3 mb-3'>
                                <div className='osc-section' style={{ borderBottom: '1px dashed #e1e1e1', paddingBottom: 10, marginBottom: 4 }}>
                                    <div className='d-flex flex-wrap justify-content-between align-items-start gap-2'>
                                        <div style={{ fontSize: 15, fontWeight: 600 }}>Order ID: {order?._id ? `KIPPS-${order._id.slice(-5)}` : 'N/A'}</div>
                                        <div style={{ fontSize: 13, color: '#555' }}>Placed: {order?.createdAt ? new Date(order.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—'}</div>
                                    </div>
                                    <div style={{ marginTop: 6, fontSize: 13, fontWeight: 600, color: order?.status === 'cancelled' ? '#b91519' : '#444' }}>Status: {statusBadge(order?.status)}</div>
                                </div>
                                <div className='osc-section pt-0'>
                                    <div className='osc-row'><span>Items</span><span>{items.length}</span></div>
                                    <div className='osc-row'><span>Subtotal</span><span>₹{subtotal}</span></div>
                                    {savings > 0 && <div className='osc-row saving'><span>You Save</span><span>-₹{savings}</span></div>}
                                    {offerLabel && <div className='osc-row offer'><span>Offer Applied</span><span>{offerLabel}</span></div>}
                                    <div className='osc-row total'><span>Payable Amount</span><span>₹{payable}</span></div>
                                </div>
                                <div className='osc-divider'></div>
                                <div className='osc-section'>
                                    <div className='osc-subtitle'>Delivery Address</div>
                                    <div className='osc-address'>{formattedAddress || '—'}</div>
                                </div>
                                <div className='osc-divider'></div>
                                <div className='osc-section items-list'>
                                    <div className='osc-subtitle mb-2'>Items</div>
                                    {items.length ? (
                                        <table className='osc-items-table'>
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '55%' }}>Product</th>
                                                    <th>Qty</th>
                                                    <th>Price</th>
                                                    <th>Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.map((it, i) => (
                                                    <tr key={i}>
                                                        <td>{it.name || it.title}</td>
                                                        <td>{it.quantity}</td>
                                                        <td>₹{it.price}</td>
                                                        <td>₹{it.price * it.quantity}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : <div style={{ fontSize: 13, color: '#666' }}>No items.</div>}
                                </div>
                            </div>
                        )}
                        {(() => {
                            if (fetching || !order) return null;
                            const status = (order.status || '').toLowerCase();
                            const cancellableStatuses = ['pending', 'processing', 'accepted'];
                            if (cancellableStatuses.includes(status)) {
                                return (
                                    <button type='button' className='checkout-btn cancel-order-btn' onClick={handleCancel} disabled={loading}>
                                        {loading ? <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span> : 'CANCEL ORDER'}
                                    </button>
                                );
                            }
                            if (status === 'delivered') {
                                return (
                                    <button type='button' className='checkout-btn cancel-order-btn' onClick={handleDownloadInvoice}>
                                        DOWNLOAD INVOICE
                                    </button>
                                );
                            }
                            return null;
                        })()}
                    </div>
                </div>
            </div>
            <div className='mt-5 pt-5 mb-5'></div>
        </Fragment>
    );
}

export default OrderDetailsPage;
